import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/PageTitle/PageTitle";
import ButtonCount from "../../components/ButtonCount/ButtonCount";
import Button from "../../components/Button/Button";
import { useEffect, useState } from "react";
import { TableSearch } from "../../components/Table/TableActions";
import Table from "../../components/Table/Table";
import { httprequest } from "../../data/api";
import { ToastContainer, toast } from "react-toastify";
import useStyles from "./styles";
import useWindowSize from "../../hooks/useWindow";
import { Drawer } from "@mui/material";
import { Icon } from "@iconify/react";

const REFERRERS_ENDPOINT = "/admin/v1/referrals/unauthenticated";

const Referrers = () => {
  const classes = useStyles();
  const [searchResult, setSearchResult] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [referrersData, setReferrersData] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const checkRowSelected = (selected) => {
    if (selected.selectedRows.length > 0) {
      setIsRowSelected(true);
      setSelectedRows(selected.selectedRows);
    } else {
      setIsRowSelected(false);
      setSelectedRows([]);
    }
  };

  const exportToCSV = (exportData, filename) => {
    try {
      if (!exportData?.length) {
        toast.error("No referrers to export");

        return;
      }

      const headers = ["Name", "Email", "Phone Number"];

      const escapeCSV = (val) => {
        if (val == null) return "";

        const stringVal = String(val);

        return `"${stringVal.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        headers.join(","),
        ...exportData.map((row) => [
          escapeCSV(row.school_name || "N/A"),
          escapeCSV(row.email || "N/A"),
          escapeCSV(row.phone_number || "N/A")
        ].join(","))
      ];

      const csvContent = `\uFEFF${csvRows.join("\n")}`;
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.length} records successfully`);
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const getReferrers = () => {
    setIsFetching(true);
    httprequest.get(REFERRERS_ENDPOINT).then((res) => {
      setIsFetching(false);
      setReferrersData(res?.data?.data?.data || []);
      setSearchResult(res?.data?.data?.data || []);
    }).catch((err) => {
      setIsFetching(false);
      toast.error(err?.response?.data?.message || "Failed to fetch referrers");
    });
  };

  const width = useWindowSize();
  const mobile = width < 600;

  const handleViewDetails = (row) => {
    setSelectedRow(row);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRow(null);
  };

  const columns = [
    {
      name: "School Name",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">School Name</p>}
          {row?.school_name}
        </>
      )
    },
    {
      name: "Email",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Email</p>}
          {row?.email}
        </>
      )
    },
    {
      name: "Phone Number",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Phone Number</p>}
          {row?.phone_number || "N/A"}
        </>
      )
    },
    {
      name: "City",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">City</p>}
          {row?.city || "N/A"}
        </>
      )
    },
    {
      name: "Relationship",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Relationship</p>}
          {row?.relationship}
        </>
      )
    },
    {
      name: "Referral Code",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Referral Code</p>}
          {row?.referral_code}
        </>
      )
    },
    {
      name: "Status",
      selector: row => (
        <>
          {mobile && <p className="mobile-styling">Status</p>}
          <span style={{ textTransform: "capitalize" }}>{row?.status}</span>
        </>
      )
    },
    {
      name: "",
      center: true,
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button onClick={() => handleViewDetails(row)} className="table-action-icon">
            <Icon icon="fluent:eye-16-regular" />
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    getReferrers();
  }, []);

  const handleSearch = (e) => {
    const { value } = e.target;
    const valArray = value.split(" ");
    const result = referrersData?.filter((data) => valArray?.every(
      (word) => data?.school_name?.toLowerCase().includes(word.toLowerCase()) ||
        data?.email?.toLowerCase().includes(word.toLowerCase()) ||
        data?.referral_code?.toLowerCase().includes(word.toLowerCase()) ||
        data?.status?.toLowerCase().includes(word.toLowerCase()) ||
        data?.city?.toLowerCase().includes(word.toLowerCase())
    ));
    setSearchResult(result);
  };

  const DrawerContent = () => (
    <div className={classes.drawerContainer}>
      <div className={classes.header}>
        <div className={classes.title}>Referrer Details</div>
        <div onClick={closeDrawer} style={{ cursor: "pointer" }}>
          <Icon icon="line-md:close-circle-filled" width="24" height="24" />
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Referrer Info</div>
        <div className={classes.flexColumn}>
          <div className={classes.gridRow}>
            <div className={classes.label}>School Name:</div>
            <div className={classes.value}>{selectedRow?.school_name || "N/A"}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Email:</div>
            <div className={classes.value}>{selectedRow?.email || "N/A"}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Phone Number:</div>
            <div className={classes.value}>{selectedRow?.phone_number || "N/A"}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>City:</div>
            <div className={classes.value}>{selectedRow?.city || "N/A"}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Relationship:</div>
            <div className={classes.value} style={{ textTransform: "capitalize" }}>
              {selectedRow?.relationship || "N/A"}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Status & Code</div>
        <div className={classes.flexColumn}>
          <div className={classes.gridRow}>
            <div className={classes.label}>Referral Code:</div>
            <div className={classes.value}>{selectedRow?.referral_code || "N/A"}</div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Status:</div>
            <div className={classes.statusBadge}>
              {selectedRow?.status || "N/A"}
            </div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Date:</div>
            <div className={classes.value}>
              {selectedRow?.created_at ? new Date(selectedRow.created_at).toLocaleDateString() : "N/A"}
            </div>
          </div>
          <div className={classes.gridRow}>
            <div className={classes.label}>Referral Link:</div>
            <div className={classes.value} style={{ wordBreak: "break-all" }}>
              <a href={selectedRow?.referral_link} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "#2563EB" }}>
                {selectedRow?.referral_link || "N/A"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout
      children={
        <>
          <PageTitle
            title="Unauthenticated Referrers"
            subtitle="Manage and view unauthenticated referrers."
          />
          <div className={classes.pageContent}>
            <div className="button-count-container">
              <ButtonCount title="Referrers Count" count={referrersData?.length || 0} />
            </div>
            <Table
              data={searchResult}
              columns={columns}
              subHeader={true}
              pagination
              isLoading={isFetching}
              handleRowSelected={checkRowSelected}
              subHeaderComponent={
                <div className="action_div">
                  <TableSearch placeholder="Search here..." searchTableFunc={handleSearch} />
                  <div style={{ display: "flex", gap: "10px" }}>
                    {isRowSelected && (
                      <Button
                        variant="primaryoutline"
                        buttonSize="fluid"
                        color="btnfontprimary"
                        onClick={() => {
                          const dateStr = new Date().toISOString().split("T")[0];
                          exportToCSV(selectedRows, `referrers_selected_${dateStr}.csv`);
                        }}
                        buttonName={`Export Selected (${selectedRows.length})`}
                      />
                    )}
                    <Button
                      variant="primary"
                      buttonSize="fluid"
                      color="btndefault"
                      onClick={() => {
                        const dateStr = new Date().toISOString().split("T")[0];
                        exportToCSV(searchResult, `referrers_all_${dateStr}.csv`);
                      }}
                      buttonName="Export"
                    />
                  </div>
                </div>
              }
            />
          </div>

          <Drawer anchor="right" open={isDrawerOpen} onClose={closeDrawer}>
            <DrawerContent />
          </Drawer>

          <ToastContainer />
        </>
      }
    />
  );
};

export default Referrers;
